import { useMemo } from 'react';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import type {
  ActionDescriptor,
  AuthoritativeFinancialResultMetadata,
  FormDescriptor,
  FormSubmissionResult,
  OperationId,
  ReportDescriptor,
  TableDescriptor,
} from '@finance/contracts';
import { FinancialDataQualityNotice } from '../financial/FinancialDataQualityNotice.tsx';
import { TableRenderer } from './TableRenderer.tsx';
import { FormRenderer } from './FormRenderer.tsx';
import { useLocalDataSource } from './table/table-data-source.ts';
import { useAppI18n } from '../../i18n/use-app-i18n';

export interface ReportRendererProps {
  contract: ReportDescriptor;
  data: Record<string, unknown>[];
  parameterForm?: FormDescriptor;
  parameters?: Record<string, unknown>;
  state?: 'CURRENT' | 'STALE' | 'PARTIAL' | 'EMPTY' | 'UNAVAILABLE';
  partialData?: boolean;
  sourceAsOf?: string | null;
  summary?: Record<string, unknown>;
  lineage?: Record<string, unknown>;
  authoritativeMetadata?: AuthoritativeFinancialResultMetadata;
  pending?: boolean;
  onRun?: (parameters: Record<string, unknown>) => Promise<FormSubmissionResult>;
  onAction?: (action: ActionDescriptor, row?: Record<string, unknown>) => void;
}

function stableText(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function safeLineage(lineage: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!lineage) return {};
  const prohibited = new Set([
    'organizationId',
    'branchId',
    'clientAccountId',
    'portfolioAccountId',
    'signedUrl',
    'storageObjectKey',
    'token',
    'secret',
  ]);
  return Object.fromEntries(Object.entries(lineage).filter(([key]) => !prohibited.has(key)));
}

function runAction(contract: ReportDescriptor): ActionDescriptor | undefined {
  const operationId = contract.operationBindings.runOperationId;
  if (!operationId) return undefined;
  return {
    id: `action.${String(contract.id)}.run`,
    schemaVersion: contract.schemaVersion,
    module: contract.module,
    feature: contract.feature,
    privacyClassification: contract.privacyClassification,
    telemetryId: contract.telemetryId,
    accessibilityLabelKey: 'a11y.action.reports.runLive',
    availability: 'AVAILABLE',
    owner: contract.owner,
    operationId: operationId as OperationId,
    labelKey: 'reports.actions.runLive',
    kind: 'command',
    submitBehavior: 'form_submit',
    submitConfig: {
      validatesForm: true,
      requiresDirty: false,
      requiresValid: true,
      resetBehavior: 'none',
    },
    intent: 'primary',
    idempotencyRequired: false,
    successBehavior: 'NO_OP',
    errorBehavior: 'SHOW_INLINE_ERROR',
    errorMessageKey: 'common.error.unexpected',
    order: 20,
    group: 'primary',
  };
}

export function ReportRenderer({
  contract,
  data,
  parameterForm,
  parameters,
  state,
  partialData,
  sourceAsOf,
  summary,
  lineage,
  authoritativeMetadata,
  pending,
  onRun,
  onAction,
}: ReportRendererProps) {
  const { text } = useAppI18n();
  const dataSource = useLocalDataSource(data);
  const csvExportAvailable = Boolean(
    contract.allowedFormats?.includes('csv') || contract.formats?.includes('csv'),
  );
  const executableParameterForm = useMemo(() => {
    const action = onRun ? runAction(contract) : undefined;
    if (!parameterForm || !action) return parameterForm;
    return {
      ...parameterForm,
      operationId: action.operationId,
      actions: [action],
      submission: {
        ...parameterForm.submission,
        defaultCommandId: action.commandId,
      },
    } as FormDescriptor;
  }, [contract, onRun, parameterForm]);

  const tableContract: TableDescriptor = {
    id: contract.id,
    schemaVersion: contract.schemaVersion,
    module: contract.module,
    feature: contract.feature,
    telemetryId: contract.telemetryId,
    accessibilityLabelKey: contract.accessibilityLabelKey,
    privacyClassification: contract.privacyClassification,
    availability: contract.availability,
    owner: contract.owner,
    titleKey: contract.titleKey,
    dataSourceId: contract.dataSourceId,
    columns: contract.columns,
    rowIdField: contract.rowIdField ?? 'id',
    pagination: { pageSize: 50 },
    capabilities: {
      sorting: true,
      filtering: true,
      globalSearch: true,
      pagination: true,
      columnVisibility: true,
      density: true,
      refresh: true,
      export: csvExportAvailable,
    },
    export: csvExportAvailable
      ? {
          formats: ['csv'],
          scopes: ['page'],
          defaultFormat: 'csv',
          defaultScope: 'page',
          formulaInjectionProtection: true,
        }
      : undefined,
  };

  const parameterLabels = (contract.parameters ?? []).map((parameter) => text(parameter.labelKey));
  const formats = contract.allowedFormats ?? contract.formats ?? [];
  const summaryEntries = Object.entries(summary ?? {});
  const projectedLineage = safeLineage(lineage);
  const lineageEntries = Object.entries(projectedLineage);

  const submit = async (_commandId: string, values: Record<string, unknown>): Promise<FormSubmissionResult> => {
    if (!onRun) return { status: 'failed' };
    return onRun(values);
  };

  return (
    <Stack spacing={2}>
      {executableParameterForm && onRun && (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <FormRenderer
            contract={executableParameterForm}
            initialValues={parameters}
            pending={pending}
            onSubmit={submit}
          />
        </Paper>
      )}

      {(parameterLabels.length > 0 || formats.length > 0 || sourceAsOf) && (
        <Paper variant="outlined">
          <Box sx={{ px: 2, py: 1.5 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}>
              {parameterLabels.length > 0 && (
                <Box>
                  <Typography variant="caption" color="text.secondary">{text('report.parameters')}</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {parameterLabels.map((label) => <Chip key={label} label={label} size="small" variant="outlined" />)}
                  </Box>
                </Box>
              )}
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                {sourceAsOf && <Chip size="small" variant="outlined" label={`${text('reportsWorkspace.live.sourceAsOf')}: ${sourceAsOf}`} />}
                {formats.map((format) => <Chip key={format} label={format.toUpperCase()} size="small" />)}
              </Stack>
            </Stack>
          </Box>
        </Paper>
      )}

      <FinancialDataQualityNotice
        metadata={authoritativeMetadata}
        state={partialData ? 'PARTIAL' : state}
        sourceAsOf={sourceAsOf}
      />
      {state === 'EMPTY' && <Alert severity="info">{text('reportsWorkspace.live.empty')}</Alert>}

      {summaryEntries.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{text('reportsWorkspace.live.summary')}</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
            {summaryEntries.map(([key, value]) => (
              <Box key={key} sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">{key}</Typography>
                <Typography sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}>{stableText(value)}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <TableRenderer
          contract={tableContract}
          dataSource={dataSource}
          onAction={onAction ? (resolved, context) => resolved && onAction(resolved.action, context.row) : undefined}
        />
      </Paper>

      {lineageEntries.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2">{text('reportsWorkspace.live.lineage')}</Typography>
          <Divider sx={{ my: 1 }} />
          <Box component="dl" sx={{ m: 0, display: 'grid', gap: 1 }}>
            {lineageEntries.map(([key, value]) => (
              <Box key={key}>
                <Typography component="dt" variant="caption" color="text.secondary">{key}</Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0, overflowWrap: 'anywhere' }}>{stableText(value)}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}
    </Stack>
  );
}
